import { useQuery } from "@apollo/client";
import { Alert, CircularProgress, Snackbar, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

import { StyledDataTable } from "@/components/molecules/StyledDataTable";
import { Loader } from "@/pages/UserDetails/styles";
import { NoData } from "@/styles/flex";

import { EditTreatmentModal } from "../EditTreatmentModal";
import { createTreatmentHistoryColumnsWithEdit } from "./columns";
import { GET_TREATMENT_HISTORY } from "./queries";
import { customTableStyles } from "./styles";
import { TreatmentHistoryResponse, TreatmentHistoryRow, TreatmentHistoryTableProps } from "./types";

const CustomNoDataOverlay = () => {
  return (
    <NoData>
      <Typography>No Program history records found</Typography>
    </NoData>
  );
};

export const TreatmentHistoryTable: React.FC<TreatmentHistoryTableProps> = ({ userId }) => {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filterText, setFilterText] = useState<string>("");
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [resetPaginationToggle, setResetPaginationToggle] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [selectedTreatment, setSelectedTreatment] = useState<TreatmentHistoryRow | null>(null);

  const { data, loading, error, refetch } = useQuery<TreatmentHistoryResponse>(
    GET_TREATMENT_HISTORY,
    {
      variables: { accountId: userId },
      fetchPolicy: "network-only",
      skip: !userId,
    }
  );

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  const treatmentHistory = (data?.treatmentHistory ?? []).map((item) => ({
    id: item.id,
    programType: item.program_type,
    roundNumber: item.round,
    startDate: item.start_date,
    endDate: item.end_date,
    specialist: item.specialist,
    status: item.status,
  }));

  const handleEdit = (row: TreatmentHistoryRow) => {
    setSelectedTreatment(row);
    setEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedTreatment(null);
  };

  const handleEditSuccess = () => {
    refetch();
  };

  const filteredItems = treatmentHistory.filter((record) => {
    const programType = record.programType.toLowerCase();
    const searchTerm = filterText.toLowerCase();
    return programType.includes(searchTerm);
  });

  const columnsWithEdit = createTreatmentHistoryColumnsWithEdit(handleEdit);

  if (loading) {
    return (
      <Loader>
        <CircularProgress />
      </Loader>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <StyledDataTable
        columns={columnsWithEdit}
        data={filteredItems}
        // defaultSortFieldId={3}
        noDataComponent={<CustomNoDataOverlay />}
        paginationResetDefaultPage={resetPaginationToggle}
        customStyles={customTableStyles}
      />

      {editModalOpen && (
        <EditTreatmentModal
          open={editModalOpen}
          onClose={handleEditModalClose}
          treatmentData={selectedTreatment}
          onSuccess={handleEditSuccess}
          account={userId}
        />
      )}

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to fetch treatment history data.
        </Alert>
      </Snackbar>
    </div>
  );
};
